import { useAutoneTranslation } from '@autone/translations';
import {
  AutoneTooltip,
  createColumnLoadingGuards,
  DataTableCellContent,
  DataTableCellTitle,
  DataTableHeaderCellWithSorting,
  DataTableIntegerValue,
  type DataTableLoadingObject,
} from '@autone/ui';
import { type ColumnHelper } from '@tanstack/react-table';
import { Info } from 'lucide-react';
import { useMemo } from 'react';

import { type APP_NAME } from '../../../consts';
import { useAssortmentTableContext } from '../contexts';

const COLUMN_ID = 'current_units' as const;

export type CurrentUnitsColumnId = typeof COLUMN_ID;

interface CurrentUnitsFields {
  inventory_units: number;
}

const { columnCellGuard, accessorFnGuard } =
  createColumnLoadingGuards<CurrentUnitsFields>();

export const createCurrentUnitsColumn = <
  T extends CurrentUnitsFields | DataTableLoadingObject,
>(
  columnHelper: ColumnHelper<T>,
) =>
  columnHelper.accessor(
    accessorFnGuard((row) => row.inventory_units),
    {
      id: COLUMN_ID,
      size: 175,
      header: function CurrentUnitsHeader({ column }) {
        const { t } = useAutoneTranslation<typeof APP_NAME>();
        const tableName = useAssortmentTableContext();

        const tooltipText = useMemo(() => {
          switch (tableName) {
            case 'productLocationsTable':
              return t(
                'table-column-tooltips.current-units.product-locations-table',
              );
            case 'skuLocationsTable':
            case 'productLocationsSkuTable':
              return t(
                'table-column-tooltips.current-units.product-locations-sku-table',
              );
            default:
              throw new Error(
                `currentUnits column not supported by ${tableName}`,
              );
          }
        }, [tableName, t]);

        const headerCellContents = useMemo(
          () => (
            <DataTableCellContent align={'right'}>
              <DataTableCellTitle className="flex flex-row gap-2 items-center uppercase font-medium whitespace-normal text-right">
                {t('table-columns.current-units')}
                <AutoneTooltip text={tooltipText}>
                  <Info className="inline" size={14} />
                </AutoneTooltip>
              </DataTableCellTitle>
            </DataTableCellContent>
          ),
          [t, tooltipText],
        );

        if (tableName === 'skuLocationsTable') return headerCellContents;

        return (
          <DataTableHeaderCellWithSorting
            column={column}
            arrowPosition="left"
            showSortable
          >
            {headerCellContents}
          </DataTableHeaderCellWithSorting>
        );
      },
      cell: (ctx) =>
        columnCellGuard({
          ctx,
          renderCell: function CurrentUnitsCell({ getValue }) {
            return (
              <DataTableCellContent align="right">
                <DataTableCellTitle>
                  <DataTableIntegerValue value={getValue()} />
                </DataTableCellTitle>
              </DataTableCellContent>
            );
          },
        }),
    },
  );
